import React, { FC } from 'react';

import { api } from '@api/reduxApi';
import { Text, Toggle } from '@components/common';
import { RestrictedAction } from '@components/shared/RestrictedAction';
import { useToaster } from '@components/stores/toaster';
import { InfoCircleIcon } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';
import { FEATURE_DETAILS, useDisabledFeatures } from '@hooks/useDisabledFeatures';
import { useFeature } from '@hooks/useFeature';

export const DisabledFeatures: FC = () => {
  const { disabledFeatures } = useDisabledFeatures();
  const [updateFeatures] = api.useUpdateAccountDisabledFeaturesMutation();

  const showToast = useToaster();

  const handleOnToggle = async (feature: ToggleableFeature) => {
    let newFeatures: ToggleableFeature[] = [];

    Object.keys(disabledFeatures).forEach((key: ToggleableFeature) => {
      if (disabledFeatures[key]) {
        newFeatures.push(key);
      }
    });

    if (newFeatures.includes(feature)) {
      newFeatures = newFeatures.filter((f) => f !== feature);
    } else {
      newFeatures = [...newFeatures, feature];
    }

    // invalid state: incentives disabled but external_recruitment enabled
    if (newFeatures.includes('incentives') && !newFeatures.includes('external_recruitment')) {
      if (feature === 'external_recruitment') {
        // just enabled external_recruitment, incentives must be enabled
        newFeatures = newFeatures.filter((f) => f !== 'incentives');
      } else if (feature === 'incentives') {
        // just disabled incentives, external_recruitment must be disabled
        newFeatures = [...newFeatures, 'external_recruitment'];
      }
    }

    try {
      await updateFeatures({ disabled_features: newFeatures }).unwrap();

      showToast({
        heading: 'Features updated',
        icon: 'success',
        text: 'Disabled features have been updated.'
      });
    } catch {
      showToast({
        heading: 'Failed to update',
        icon: 'error',
        text: 'Something went wrong updating disabled features. Please try again later.'
      });
    }
  };

  return (
    <section>
      <Text h='600' mb='1' bold>
        Feature access
      </Text>
      <Text h='400' color='gray-500' mb='4'>
        Decide which features users on your account have access to. When toggled off, users won&apos;t see these
        capabilities in Great Question.
      </Text>

      <div>
        {Object.keys(FEATURE_DETAILS)
          .filter((feature) => !FEATURE_DETAILS[feature].hidden)
          .map((feature: ToggleableFeature) => (
            <RestrictedAction key={feature} permission='disableFeatures'>
              {({ can, may }) => (
                <div className='flex flex-row items-center space-x-2'>
                  <Toggle
                    on={!disabledFeatures[feature]}
                    disabled={!can || !may}
                    onToggle={() => handleOnToggle(feature)}
                    name={FEATURE_DETAILS[feature].title}
                  />
                  <div className='flex items-center space-x-2'>
                    <Text h='400' color={can && may ? 'gray-700' : 'gray-500'}>
                      {FEATURE_DETAILS[feature].title}
                    </Text>
                    <Tooltip
                      className='cursor-pointer'
                      content={FEATURE_DETAILS[feature].description}
                      placement='right'
                    >
                      <InfoCircleIcon className='h-3 w-3' />
                    </Tooltip>
                  </div>
                </div>
              )}
            </RestrictedAction>
          ))}
      </div>
    </section>
  );
};
